import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Building2, ArrowLeft, Save, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    notes: "",
    default_currency: "SEK"
  });

  useEffect(() => {
    if (isEditing) {
      fetchSupplier();
    }
  }, [id]);

  const fetchSupplier = async () => {
    try {
      const response = await axios.get(`${API}/purchasing/suppliers/${id}`);
      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching supplier:", error);
      toast.error("Kunde inte hämta leverantör");
      navigate("/purchasing/suppliers");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name.trim()) {
      toast.error("Företagsnamn är obligatoriskt");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/purchasing/suppliers/${id}`, formData);
        toast.success("Leverantör uppdaterad");
      } else {
        await axios.post(`${API}/purchasing/suppliers`, formData);
        toast.success("Leverantör skapad");
      }
      navigate("/purchasing/suppliers");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte spara leverantör");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Link to="/purchasing/suppliers">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-blue-500" />
                <h1 className="font-heading text-xl font-bold uppercase tracking-tight">
                  {isEditing ? "Redigera leverantör" : "Ny leverantör"}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-2xl">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="font-heading uppercase">Leverantörsuppgifter</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">
                  Företagsnamn <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleChange("company_name", e.target.value)}
                  placeholder="T.ex. ABC Grossist AB"
                  data-testid="company-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Kontaktperson</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => handleChange("contact_person", e.target.value)}
                    placeholder="T.ex. Anna Andersson"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    placeholder="T.ex. 08-123 456"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-post</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="T.ex. order@abcgrossist.se"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="default_currency">Standardvaluta</Label>
                  <Select 
                    value={formData.default_currency || "SEK"} 
                    onValueChange={(value) => handleChange("default_currency", value)}
                  >
                    <SelectTrigger id="default_currency">
                      <SelectValue placeholder="Välj valuta" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SEK">SEK - Svenska kronor</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="USD">USD - US Dollar</SelectItem>
                      <SelectItem value="DKK">DKK - Danska kronor</SelectItem>
                      <SelectItem value="NOK">NOK - Norska kronor</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Förval när du lägger in inköpspriser
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Adress</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="T.ex. Industrivägen 1, 123 45 Stockholm"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Anteckningar</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Övriga anteckningar..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 mt-6">
            <Link to="/purchasing/suppliers">
              <Button type="button" variant="outline">
                Avbryt
              </Button>
            </Link>
            <Button type="submit" disabled={saving} className="gap-2" data-testid="save-supplier-btn">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sparar...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditing ? "Spara ändringar" : "Skapa leverantör"}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
